import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faApplePay, faPaypal, faGooglePay} from '@fortawesome/free-brands-svg-icons';
import {CreditCard} from "lucide-react";
import {BaseModal} from "./ModalBase";
import {Button} from "../ui/Button";
import {Typography} from "../ui/Typography";
import React from "react";
import {CreditPackageData} from "./CreditPricingModal";

export interface PAYMENT_OPTION_DATA {
    name: string;
    icon: React.ComponentType<{ className?: string }>;
}

interface CreditPackage {
    name: string;
    totalCredits: number;
    totalPrice: number;
}

interface CheckoutModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedPackage: CreditPackageData | null;
}

export const CheckoutModal = ({isOpen, onClose, selectedPackage}: CheckoutModalProps) => {
    if (!isOpen) return null;
    return (
        <BaseModal isOpen={isOpen} onClose={onClose}>
            
            <div id="modal-container"
                 className="relative bg-surface-900 rounded-xl shadow-2xl w-full max-w-5xl transform transition-all">

                <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr]">

                    {/* pass in selectedPackage where needed */}
                    <div className="p-8 border-r border-surface-800 overflow-y-auto max-h-[90vh]">
                        <ContactInfoSection/>
                        <PaymentInfoSection/>
                    </div>

                    <OrderSummarySection/>
                    
                </div>
            </div>
        </BaseModal>
    );
};

const ContactInfoSection = (any) => {
    return (
        <div className="mb-6" id="guest-checkout">

            <Typography as="h2" variant="h2" className="mb-1">
                Contact information
            </Typography>

            <Typography as="p" variant="p" className="mt-2">
                Need an account? 
                <Button
                    intent="link"
                    // className="ml-2 mt-2"
                >
                    Sign up
                </Button>
            </Typography>
            
            
            <div className="space-y-5">
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-surface-50 mb-2">Email Address</label>
                    <input type="email" id="email" autoComplete="email" placeholder="you@example.com"
                           className="w-full p-3 rounded-lg border border-surface-800 bg-[var(--bg-input)] input-focus-ring text-[var(--text-body)] placeholder:text-surface-300 transition-all" />
                </div>
                <div>
                    <label htmlFor="password" className="block text-sm font-medium text-surface-50 mb-2">
                        Create a Password
                    </label>
                    <input 
                        type="password" 
                        id="password" 
                        autoComplete="new-password" 
                        placeholder="••••••••"
                        
                        className="w-full p-3 rounded-lg border border-surface-800 bg-[var(--bg-input)] 
                        input-focus-ring text-[var(--text-body)] placeholder:text-surface-300 transition-all" />
                    
                        <p className="text-xs text-surface-300 mt-2">
                            Your account will be created automatically upon purchase.
                        </p>
                </div>
            </div>

            <hr className="border-t border-surface-800 my-8" />
        </div>
    );
};

export const PaymentInfoSection = (any) => {
    return (
        <section className="payment-details">
            <h2 className="text-2xl font-bold text-surface-50 mb-6">
                Payment Information
            </h2>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                
                <button data-payment="credit-card"
                        className="payment-method-btn flex flex-col items-center justify-center p-4 rounded-lg border-2 bg-[var(--bg-input)] transition-all border-brand-500 text-brand-500">
                    <CreditCard className="text-2xl mb-2"/>
                    <span className="text-sm font-semibold">Card</span>
                </button>
                
                <button data-payment="paypal"
                        className="payment-method-btn flex flex-col items-center justify-center p-4 rounded-lg border-2 border-surface-800 hover:border-surface-300 bg-[var(--bg-input)] transition-all text-[var(--text-body)]">
                    <FontAwesomeIcon
                        icon={faPaypal}
                        className="text-2xl mb-2"
                    />
                    <span className="text-sm font-semibold">PayPal</span>
                </button>
                
                <button data-payment="apple-pay"
                        className="payment-method-btn flex flex-col items-center justify-center p-4 rounded-lg border-2 border-surface-800 hover:border-surface-300 bg-[var(--bg-input)] transition-all text-[var(--text-body)]">
                    <FontAwesomeIcon
                        icon={faApplePay}
                        className="text-2xl mb-2"
                    />
                    <span className="text-sm font-semibold">Apple Pay</span>
                </button>
                
                <button data-payment="google-pay"
                        className="payment-method-btn flex flex-col items-center justify-center p-4 rounded-lg border-2 border-surface-800 hover:border-surface-300 bg-[var(--bg-input)] transition-all text-[var(--text-body)]">
                    <FontAwesomeIcon
                        icon={faGooglePay}
                        className="text-2xl mb-2"
                    />
                    <span className="text-sm font-semibold">Google Pay</span>
                </button>
                
            </div>

            <form className="payment-content" id="credit-card-form">
                <div className="mb-5 relative">
                    <label htmlFor="card-number" className="block text-sm font-medium text-surface-50 mb-2">Card
                        Number</label>
                    <input type="text" id="card-number" placeholder="0000 0000 0000 0000"
                           className="w-full p-3 pl-12 rounded-lg border border-surface-800 bg-[var(--bg-input)] input-focus-ring text-[var(--text-body)] placeholder:text-surface-300 transition-all"/>
                    <i className="fa-solid fa-credit-card absolute left-4 top-1/2 -translate-y-1/2 mt-[10px] text-surface-300"></i>
                </div>
                <div className="grid grid-cols-2 gap-5 mb-5">
                    <div>
                        <label htmlFor="expiration-date"
                               className="block text-sm font-medium text-surface-50 mb-2">Expiration</label>
                        <input type="text" id="expiration-date" placeholder="MM / YY"
                               className="w-full p-3 rounded-lg border border-surface-800 bg-[var(--bg-input)] input-focus-ring text-[var(--text-body)] placeholder:text-surface-300 transition-all"/>
                    </div>
                    <div>
                        <label htmlFor="cvc" className="block text-sm font-medium text-surface-50 mb-2">CVC</label>
                        <input type="text" id="cvc" placeholder="123"
                               className="w-full p-3 rounded-lg border border-surface-800 bg-[var(--bg-input)] input-focus-ring text-[var(--text-body)] placeholder:text-surface-300 transition-all"/>
                    </div>
                </div>
                <div className="mb-6">
                    <label htmlFor="cardholder-name" className="block text-sm font-medium text-surface-50 mb-2">Cardholder
                        Name</label>
                    <input type="text" id="cardholder-name" placeholder="John Doe"
                           className="w-full p-3 rounded-lg border border-surface-800 bg-[var(--bg-input)] input-focus-ring text-[var(--text-body)] placeholder:text-surface-300 transition-all"/>
                </div>
                
                <div className="flex items-center justify-between mb-8">
                    <span className="text-sm font-medium text-surface-50">Remember this card</span>
                    <label className="switch">
                        <input type="checkbox" defaultChecked />
                        <span className="slider"></span>
                    </label>
                </div>

                <button type="submit"
                        className="w-full h-14 bg-brand-500 hover:bg-brand-500 text-black text-lg font-bold rounded-lg transition-colors flex items-center justify-center">
                    <i className="fa-solid fa-lock mr-2"></i> Pay $29.99 Now
                </button>
                {/*<p className="text-center text-sm text-surface-300 mt-4">*/}
                {/*    Secure payment powered by Stripe*/}
                {/*</p>*/}
            </form>
            
            <PaymentContent
                id={"paypal-form"}
                text={"You will be redirected to PayPal to complete your purchase."}
            />

            <PaymentContent
                id={"apple-pay-form"}
                text={"Follow the instructions on your device to complete your purchase with Apple Pay."}
            />
            
            <PaymentContent 
                id={"google-pay-form"} 
                text={"Follow the instructions on your device to complete your purchase with Google Pay."} 
            />
            
        </section>
    );
};

export const PaymentContent = ({id, text}) => {
    const Id = id as string;
    const Text = text as string;
    
    return (
        <div className="payment-content hidden text-center p-6" id={Id}>
            
            <Button 
                intent="primary"
                size="large"
                type="submit"
                hover={true}
             />
            
            <p className="text-[var(--text-body)]">
                {Text}.
            </p>
        </div>
    );
};


export const OrderSummarySection = (any) => {
    return (
        <div className="p-6 bg-[var(--bg-input)]/50 rounded-r-xl hidden lg:block">
            <aside className="h-full flex flex-col">
                <h2 className="text-2xl font-bold text-surface-50 mb-6">
                    Order Summary
                </h2>

                <div className="space-y-6 mb-6 flex-grow">
                    <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-[var(--bg-input)] rounded-lg flex-shrink-0">
                            <img 
                                src="https://placehold.co/128x128/1a1a1a/e0e0e0?text=P" 
                                 alt="Pro Plan Icon"
                                 className="w-full h-full object-cover rounded-lg"
                            />
                        </div>
                        <div>
                            <h3 className="font-semibold text-surface-50">
                                Pro Plan - Yearly
                            </h3>
                            <p className="text-sm text-[var(--text-muted)]">
                                Billed once per year
                            </p>
                        </div>
                        <span className="ml-auto font-semibold text-surface-50">$29.99</span>
                    </div>
                </div>

                <div className="flex justify-between items-center text-[var(--text-body)] mb-4">
                    <span>Subtotal</span>
                    <span>$29.99</span>
                </div>

                <div className="flex mb-6">
                    <input type="text" placeholder="Discount Code"
                           className="w-full p-3 rounded-l-lg border border-r-0 border-surface-800 bg-[var(--bg-input)] input-focus-ring text-[var(--text-body)] placeholder:text-[var(--text-muted)] transition-all"/>
                    <button
                        className="bg-[var(--bg-input)] text-[var(--text-body)] font-semibold px-5 rounded-r-lg border border-surface-800 hover:bg-surface-800 transition-colors">
                        Apply
                    </button>
                </div>

                <hr className="border-t border-surface-800 mb-6"/>

                <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-surface-50">Total</span>
                    <span className="text-3xl font-bold text-brand-500">$29.99</span>
                </div>
            </aside>
        </div>
    );
};


















































